import { InteractiveContainer } from "@jugl-web/ui-components";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Virtualizer } from "virtua";
import { getCountryNameByIsoCode } from "../../utils";
import { WidgetEmptyStateLabel } from "../WidgetEmptyStateLabel";
import type { ImpactOverviewBodyProps } from "./types";

export const ImpactOverviewBody: FC<ImpactOverviewBodyProps> = ({
  data,
  impactDistributionData,
  impactDetailsById,
  rootClassName,
}) => {
  const { isMobile } = useAppVariant();
  const getInitialImpactId = useCallback(
    () => impactDistributionData[0]?.impactType ?? null,
    [impactDistributionData]
  );

  const [selectedImpactId, setSelectedImpactId] = useState<string | null>(
    getInitialImpactId
  );

  const { t } = useTranslations();

  useEffect(() => {
    if (selectedImpactId && !impactDetailsById[selectedImpactId]) {
      setSelectedImpactId(getInitialImpactId());
    }
  }, [getInitialImpactId, impactDetailsById, selectedImpactId]);

  const rowGridClassName = cx(
    "group grid",
    isMobile
      ? "min-w-full w-[860px] grid-cols-[120px_170px_120px_110px_140px_120px_80px]"
      : "w-full grid-cols-[minmax(100px,1.2fr)_minmax(120px,1.35fr)_minmax(88px,0.85fr)_minmax(72px,0.65fr)_minmax(96px,0.95fr)_minmax(80px,0.75fr)_minmax(56px,0.5fr)]"
  );

  const bodyRowHoverClassName = "transition-colors group-hover:bg-grey-100";

  const headerCellClassName =
    "border-0 border-b border-r border-solid border-[#F3F2F3] bg-white px-4 py-3 text-left text-xs font-normal text-dark-700 last:border-r-0 font-secondary";

  const bodyCellClassName =
    "min-w-0 overflow-hidden border-0 border-b border-r border-solid border-[#F3F2F3] px-4 py-5 text-left text-sm text-dark last:border-r-0";

  const filteredData = useMemo(() => {
    if (!selectedImpactId) {
      return data;
    }

    return data.filter((item) => item.impact === selectedImpactId);
  }, [data, selectedImpactId]);

  const virtualRows = useMemo(
    () =>
      filteredData.map((item) => {
        const countryLabel = item.country
          ? getCountryNameByIsoCode(item.country)
          : "Unknown";

        return (
          <div key={item.id} className={rowGridClassName}>
            <div
              className={cx(
                bodyCellClassName,
                bodyRowHoverClassName,
                "min-w-0"
              )}
            >
              <span className="block truncate font-medium" title={item.topic}>
                {item.topic}
              </span>
            </div>
            <div
              className={cx(
                bodyCellClassName,
                bodyRowHoverClassName,
                "min-w-0"
              )}
            >
              <span className="block truncate" title={item.subtopic}>
                {item.subtopic}
              </span>
            </div>
            <div className={cx(bodyCellClassName, bodyRowHoverClassName)}>
              <span className="block truncate" title={item.channel}>
                {item.channel}
              </span>
            </div>
            <div className={cx(bodyCellClassName, bodyRowHoverClassName)}>
              <span className="block truncate" title={countryLabel}>
                {countryLabel}
              </span>
            </div>
            <div className={cx(bodyCellClassName, bodyRowHoverClassName)}>
              <div className="flex min-w-0 items-center gap-2">
                <div
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{
                    backgroundColor:
                      impactDetailsById[item.impact]?.color || "#000000",
                  }}
                  aria-hidden
                />
                <span
                  className="min-w-0 truncate font-medium"
                  title={item.impact}
                >
                  {item.impact}
                </span>
              </div>
            </div>
            <div
              className={cx(
                bodyCellClassName,
                bodyRowHoverClassName,
                item.sentiment === "Negative" &&
                  "bg-tertiary-50 group-hover:bg-tertiary-50"
              )}
            >
              <span className="block truncate" title={item.sentiment}>
                {item.sentiment}
              </span>
            </div>
            <div
              className={cx(
                bodyCellClassName,
                bodyRowHoverClassName,
                "text-center"
              )}
            >
              {item.count}
            </div>
          </div>
        );
      }),
    [filteredData, impactDetailsById, rowGridClassName]
  );

  const isEmpty = data.length === 0;

  const content = useMemo(() => {
    if (isEmpty) {
      return (
        <div className="flex h-full min-h-[200px] items-center justify-center">
          <WidgetEmptyStateLabel />
        </div>
      );
    }

    return (
      <div className="flex min-h-0 min-w-0 flex-col gap-8">
        <div
          className={cx(
            "flex flex-wrap items-center gap-2 px-5",
            isMobile && "flex-nowrap overflow-x-auto px-0 pb-2.5"
          )}
        >
          {impactDistributionData.map((item) => (
            <BusinessImpactPill
              key={item.impactType}
              color={item.color}
              name={item.impactType}
              isActive={item.impactType === selectedImpactId}
              totalCount={item.chatsCount}
              onSelect={() => setSelectedImpactId(item.impactType)}
            />
          ))}
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div
            className={cx(
              "jugl__custom-scrollbar min-h-0 min-w-0 flex-1 overflow-auto px-5",
              isMobile && "px-0"
            )}
          >
            <div className={cx("sticky top-0 z-10", rowGridClassName)}>
              <div className={headerCellClassName}>
                {t({
                  id: "cx-page.topics-impact-overview-column-topic",
                  defaultMessage: "Topic",
                })}
              </div>
              <div className={headerCellClassName}>
                {t({
                  id: "cx-page.topics-impact-overview-column-subtopic",
                  defaultMessage: "Subtopic",
                })}
              </div>
              <div className={headerCellClassName}>
                {t({
                  id: "cx-page.topics-impact-overview-column-channel",
                  defaultMessage: "Channel",
                })}
              </div>
              <div className={headerCellClassName}>
                {t({ id: "common.country", defaultMessage: "Country" })}
              </div>
              <div className={headerCellClassName}>
                {t({
                  id: "cx-page.topics-impact-overview-column-impact",
                  defaultMessage: "Impact",
                })}
              </div>
              <div className={headerCellClassName}>
                {t({
                  id: "cx-page.topics-impact-overview-column-sentiment",
                  defaultMessage: "Sentiment",
                })}
              </div>
              <div className={cx(headerCellClassName, "text-center")}>
                {t({
                  id: "cx-page.topics-impact-overview-column-sessions",
                  defaultMessage: "Sessions",
                })}
              </div>
            </div>
            <Virtualizer overscan={8}>{virtualRows}</Virtualizer>
          </div>
        </div>
      </div>
    );
  }, [
    impactDistributionData,
    isEmpty,
    isMobile,
    rowGridClassName,
    selectedImpactId,
    t,
    virtualRows,
  ]);

  return (
    <div className={cx("flex min-h-0 min-w-0 flex-1 flex-col", rootClassName)}>
      {content}
    </div>
  );
};

interface BusinessImpactPillProps {
  color: string;
  name: string;
  isActive: boolean;
  totalCount: number;
  onSelect: () => void;
}

const BusinessImpactPill: FC<BusinessImpactPillProps> = ({
  color,
  name,
  isActive,
  totalCount,
  onSelect,
}) => {
  return (
    <InteractiveContainer
      className={cx(
        "jugl__focusable-outline font-secondary flex h-8 shrink-0 items-center gap-2 rounded-[10px] px-5 transition-colors",
        isActive
          ? "bg-dark text-white"
          : "bg-grey-100 text-dark-700 hover:bg-grey-200"
      )}
      isDisabled={isActive}
      onClick={onSelect}
    >
      <div
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span
        className={cx("text-[13px]", isActive ? "font-medium" : "font-normal")}
      >
        {name}
      </span>
      <div
        className={cx(
          "flex items-center justify-center rounded-xl px-1.5 text-xs font-medium leading-none transition-colors",
          isActive ? "bg-dark-800 text-grey-100" : "bg-dark/5 text-grey-900"
        )}
      >
        {totalCount}
      </div>
    </InteractiveContainer>
  );
};
